'use client';

import {Modal, Stack, Text} from '@eds-web/react';
import {useLocale, useMessages} from '@epic-mw/i18n';
import {useEffect} from 'react';
import {ModalLoading} from '../loading/ModalLoading';
import {emitEvent} from '@/lib/utils/analytics';
import {css} from '@panda/css';
import {RedeemPinType} from '@/types';
import {LoadingSpinner} from '@/components/atoms';
import {applySetLangToHref} from '@/lib/utils/setLang.utils';

const modalStyles = css({
	alignItems: 'center',
	textAlign: 'center',
	width: '503px !important',
	border: 'none !important',
	// bg: "#101014 !important",
	// "& > div": {
	// 	bg: "#101014 !important",
	// 	justifyContent: "center !important",
	// },
	smDown: {
		width: 'unset !important'
	}
});

interface RedirectModalProps {
	open: boolean;
	title: string;
	desc: string;
	imgSrc: string;
	redirectTo: string;
	isLoading?: boolean;
	onClose?: () => void;
	pinType: RedeemPinType;
}

export const RedirectModal = ({
	open,
	isLoading,
	pinType,
	onClose,
	imgSrc,
	title,
	desc,
	redirectTo
}: RedirectModalProps) => {
	const locale = useLocale();
	const messages = useMessages();

	useEffect(() => {
		if (!open) return;
		emitEvent(
			{
				eventCategory: 'pageview',
				interactionType: 'pageview',
				eventLabel: 'PageView_V2',
				eventValue: {
					subView: `Redirect ${pinType}`,
					locale
				}
			},
			'PageView'
		);
	}, [open]);

	useEffect(() => {
		const timer = setTimeout(() => {
			onClose?.();
			window.location.href = applySetLangToHref(redirectTo);
			// If the initial router.push call is interrupted, subsequent router.push attempts will fail here.
			// ^Simple and avoids SPA router entirely (since we're navigating externally, it doesn't matter), so interruptions don't leave the redeem app in a bad state.
		}, 3000);
		return () => clearTimeout(timer);
	}, [redirectTo]);

	return (
		<Modal
			className={modalStyles}
			hideXButton
			size="md"
			containerProps={{className: css({zIndex: '9000 !important', maxWidth: '100%'})}}
			isOpen={open}
			onOpenChange={(isOpen) => {
				// seems like this is never called when `isOpen === true` :think:
				if (!isOpen) onClose?.();
			}}
			aria-labelledby={title}
			closeLabel={messages['epic.app.close'] || 'Close'}
			closeButton={undefined}
			content={
				<div>
					{isLoading && <ModalLoading />}
					<Stack gap={32}>
						{imgSrc && (
							<img
								src={imgSrc}
								alt={title}
								width={200}
								height="auto"
								style={{
									objectFit: 'cover',
									display: 'block',
									margin: '0 auto'
								}}
							/>
						)}

						<Stack gap={8}>
							<Text variant="headingLG">{title}</Text>
							<Text variant="uiMD" color="secondary">
								{desc}
							</Text>
						</Stack>

						<Stack gap={8} align="center" justify="center" direction="row" noWrap>
							<LoadingSpinner width={24} height={24} />
							<Text variant="uiLGBold" style={{color: '#26BBFF'}}>
								{messages['epic.app.modal.redirectingNow']}
							</Text>
						</Stack>
					</Stack>
				</div>
			}
		/>
	);
};
